import React from 'react'
import { Tooltip, theme } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { FormContext } from 'antd/es/form/context'
import { useLocale } from 'antd/es/locale'
import { fontSize, lineHeight, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import { safeFieldId } from './fieldId'
import { FieldGroupContext } from './FieldGroupContext'
import { VISUALLY_HIDDEN_CLASS } from './visuallyHidden'
import type { FieldRequirement } from './types'
import type { StackDirection } from '../Stack/Stack'

export interface FieldGroupProps {
  label: React.ReactNode
  hideLabel?: boolean
  description?: React.ReactNode
  tooltip?: React.ReactNode
  requirement?: FieldRequirement
  /** The cross-field message; a member's own rule message stays under that member. */
  error?: React.ReactNode
  /** Advisory, and never marks the members invalid - a rollover notice, not a rejection. */
  warning?: React.ReactNode
  /** @default 'horizontal' */
  direction?: StackDirection
  /** @default 'xs' */
  gap?: keyof typeof spacing
  children: React.ReactNode
  style?: React.CSSProperties
  className?: string
}

/**
 * One label over several controls reading as a single field - a month beside a day, a start beside
 * an end. `role="group"` with `aria-labelledby` rather than `<label htmlFor>`, which can only
 * point at one control. Each member keeps its own accessible name.
 */
export function FieldGroup({
  label,
  hideLabel = false,
  description,
  tooltip,
  requirement = 'none',
  error,
  warning,
  direction = 'horizontal',
  gap = 'xs',
  children,
  style,
  className,
}: FieldGroupProps) {
  const { token } = theme.useToken()
  const { requiredMark } = React.useContext(FormContext)
  const [formLocale] = useLocale('Form')
  const id = safeFieldId(React.useId())

  const labelId = `${id}-label`
  const descriptionId = `${id}-description`
  const messageId = `${id}-message`
  const invalid = error !== undefined
  const message = error ?? warning
  const showOptionalMark = requiredMark !== false && requirement === 'optional'

  const contextValue = React.useMemo(() => ({ messageId: message !== undefined ? messageId : undefined, invalid }), [invalid, message, messageId])

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xxs, ...style }}>
      <span
        id={labelId}
        className={hideLabel ? VISUALLY_HIDDEN_CLASS : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: spacing.xxs,
          fontSize: fontSize.base,
          lineHeight: lineHeight.base,
          color: token.colorText,
        }}
      >
        {label}
        {showOptionalMark && <span style={{ color: token.colorTextDescription }}>{formLocale?.optional}</span>}
        {tooltip !== undefined && (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined aria-hidden style={{ color: token.colorTextSecondary }} />
          </Tooltip>
        )}
      </span>

      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={[description !== undefined ? descriptionId : undefined, message !== undefined ? messageId : undefined].filter(Boolean).join(' ') || undefined}
        style={{
          display: 'flex',
          flexDirection: direction === 'vertical' ? 'column' : 'row',
          gap: spacing[gap],
          alignItems: direction === 'horizontal' ? 'flex-start' : undefined,
        }}
      >
        <FieldGroupContext.Provider value={contextValue}>{children}</FieldGroupContext.Provider>
      </div>

      {description !== undefined && (
        <div id={descriptionId} style={{ fontSize: fontSize.sm, lineHeight: lineHeight.base, color: token.colorTextDescription }}>
          {description}
        </div>
      )}

      {message !== undefined && (
        <div
          id={messageId}
          role="alert"
          aria-live="polite"
          style={{
            fontSize: fontSize.sm,
            lineHeight: lineHeight.base,
            color: invalid ? token.colorErrorText : token.colorWarningText,
          }}
        >
          {message}
        </div>
      )}
    </div>
  )
}

export default FieldGroup
